"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import Intercom, { shutdown, update, hide } from "@intercom/messenger-js-sdk";
import { getLanguageOverride } from "@marketing-sites/utils/intercom-locale";
import { useLogicalPathname } from "@/lib/l10n/providers";

const INTERCOM_APP_ID = "tx2p130c";
const TEAM_WEB_APP_ID = "pi9zubh0";

// Matched against the locale-stripped logical path. The interim locale dirs mount
// no LocaleProvider, so their paths keep the prefix and need their own entries.
export const DISALLOWED_ROUTES = [
  "/view-demos",
  "/contact-sales",
  "/ja-jp/view-demos",
  "/ja-jp/contact-sales",
  "/signup",
  "/intercom-link/support",
  "/customer-transformation-report",
];

const VIEW_DEMOS_ROUTE = "/view-demos";

// hide() leaves a sticky hidden state update() can't clear, so skip it on routes a visitor navigates away from — hide_default_launcher still hides the launcher there and stays reversible on soft-nav.
function keepsLauncherRevealable(logicalPathname: string) {
  return (
    logicalPathname === VIEW_DEMOS_ROUTE ||
    logicalPathname.endsWith("/contact-sales")
  );
}

const VIEW_DEMOS_GATE_STORAGE_KEY = "form-field-view-demos-gate-submitted";
const VIEW_DEMOS_GATE_BYPASS_REFERRERS = new Set([
  "fin-view-demo-post-gate",
  "signup-waitlist-view-demo-post-gate",
]);

function isViewDemosGateOpen() {
  if (typeof window === "undefined") return true;
  const referrer = new URLSearchParams(window.location.search).get("referrer");
  if (referrer && VIEW_DEMOS_GATE_BYPASS_REFERRERS.has(referrer)) {
    return false;
  }
  return window.sessionStorage.getItem(VIEW_DEMOS_GATE_STORAGE_KEY) !== "true";
}

export function shouldHideLauncher(pathname: string) {
  if (pathname === VIEW_DEMOS_ROUTE) {
    return isViewDemosGateOpen();
  }
  return DISALLOWED_ROUTES.some((route) => pathname.startsWith(route));
}

let lastRequestAt = 0;

// The widget script boots lazily, so an update() call issued before it lands
// gets queued and can be lost. Writing the setting directly onto
// intercomSettings makes it survive that boot regardless of queue timing.
function syncLauncherSetting(hideLauncher: boolean) {
  if (typeof window !== "undefined" && window.intercomSettings) {
    window.intercomSettings.hide_default_launcher = hideLauncher;
  }
}

function syncLanguageOverride(languageOverride: string | null) {
  if (typeof window !== "undefined" && window.intercomSettings) {
    Object.assign(window.intercomSettings, {
      language_override: languageOverride,
    });
  }
}

const LANGUAGE_OVERRIDE_STORAGE_KEY = "intercom-messenger-language-override";

// The widget resumes its last language from localStorage on every boot; shutdown() can't reach that before this page's first boot exists, so this drops the stale entry directly.
// The marker lives in localStorage too so a new tab still sees it; with no marker the cached language is unknown, so it resets.
function resetIfLanguageChanged(
  appId: string,
  languageOverride: string | null,
) {
  const previous = window.localStorage.getItem(LANGUAGE_OVERRIDE_STORAGE_KEY);
  if (previous !== (languageOverride ?? "")) {
    window.localStorage.removeItem(`intercom.intercom-state-${appId}`);
  }
  window.localStorage.setItem(
    LANGUAGE_OVERRIDE_STORAGE_KEY,
    languageOverride ?? "",
  );
}

export function updateIntercomMessenger(logicalPathname: string) {
  if (typeof window === "undefined" || !window.Intercom) return;

  const hideLauncher = shouldHideLauncher(logicalPathname);
  lastRequestAt = Math.max(Math.floor(Date.now() / 1000), lastRequestAt + 1);
  syncLauncherSetting(hideLauncher);
  update({
    hide_default_launcher: hideLauncher,
    last_request_at: lastRequestAt,
  });
}

export function IntercomMessenger() {
  // Both are needed: launcher rules match unprefixed routes, while the Messenger
  // language is derived from the locale prefix the raw URL still carries.
  const pathname = usePathname();
  const logicalPathname = useLogicalPathname();
  const isInitialized = useRef(false);

  // Cleanup only on unmount
  useEffect(() => {
    return () => {
      shutdown();
    };
  }, []);

  useEffect(() => {
    // Don't initialize if in iframe
    if (typeof window !== "undefined" && window.self !== window.top) {
      return;
    }

    const appId =
      process.env.NODE_ENV === "production" ? INTERCOM_APP_ID : TEAM_WEB_APP_ID;

    const hideLauncher = shouldHideLauncher(logicalPathname);
    const languageOverride = getLanguageOverride(pathname);

    if (!isInitialized.current) {
      resetIfLanguageChanged(appId, languageOverride ?? null);
      Intercom({
        app_id: appId,
        hide_default_launcher: hideLauncher,
        // SDK types this string-only; explicit null clears the override like update() does.
        language_override: (languageOverride ?? null) as string | undefined,
      });
      if (hideLauncher && !keepsLauncherRevealable(logicalPathname)) {
        hide();
      }
      isInitialized.current = true;
    } else {
      /*
       * Sending `last_request_at` guarantees a changed payload, forcing a re-ping against the new URL.
       * See Intercom help article: https://www.intercom.com/help/en/articles/170-integrate-intercom-in-a-single-page-app
       */
      lastRequestAt = Math.max(
        Math.floor(Date.now() / 1000),
        lastRequestAt + 1,
      );
      syncLauncherSetting(hideLauncher);
      syncLanguageOverride(languageOverride ?? null);
      update({
        hide_default_launcher: hideLauncher,
        last_request_at: lastRequestAt,
        // Explicit null, not an omitted key: Intercom keeps the last override it
        // was given, so leaving this out would strand a locale route's language
        // on every subsequent non-locale route.
        language_override: languageOverride ?? null,
      });
    }
  }, [pathname, logicalPathname]);

  return null;
}
